/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Attachment } from '@src/app/model/attachment';
import { PickAttachmentDialogComponent, PickAttachmentDialogData } from '@src/app/shared/dialogs/pick-attachment-dialog/pick-attachment-dialog.component';

export const MAX_MAIL_ATTACHMENT_BYTES = 9.5 * 1024 * 1024;

@Component({
    selector: 'app-mail-attachment-chips',
    imports: [MatIconModule, MatButtonModule, MatTooltip],
    templateUrl: './mail-attachment-chips.component.html',
    styleUrl: './mail-attachment-chips.component.scss',
})
export class MailAttachmentChipsComponent {
    @Input() availableAttachments: Attachment[] = [];
    @Input() attachments: Attachment[] = [];
    @Output() attachmentsChange = new EventEmitter<Attachment[]>();

    dialog = inject(MatDialog);

    get totalBytes(): number {
        return this.attachments.reduce((sum, a) => sum + (a.size || 0), 0);
    }

    get sizeExceeded(): boolean {
        return this.totalBytes > MAX_MAIL_ATTACHMENT_BYTES;
    }

    get totalSizeMb(): string {
        return (this.totalBytes / (1024 * 1024)).toFixed(1);
    }

    addAttachment() {
        this.dialog.open(PickAttachmentDialogComponent, {
            data: { attachments: this.availableAttachments, title: $localize`Add Attachment` } as PickAttachmentDialogData,
        }).afterClosed().subscribe((att: Attachment) => {
            if (!att) return;
            if (this.attachments.find(a => a.id === att.id)) return;
            this.attachmentsChange.emit([...this.attachments, att]);
        });
    }

    removeAttachment(att: Attachment) {
        this.attachmentsChange.emit(this.attachments.filter(a => a.id !== att.id));
    }
}
